import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import confetti from "canvas-confetti";

// Celebration shown the moment an enrollment flips to complete on a track
// that awards a badge. Purely presentational: the actual EarnedBadge row is
// awarded by the enrollment-progress Celery task (lms_info.badges), so this
// fires off the track-completion transition the client already observes
// rather than waiting on the async row.
//
// Confetti pattern mirrors swipe_deck_player/CompletionScreen.js: bind
// canvas-confetti to a local canvas so particles stay inside the modal
// stacking context.

const style = {
  position: "absolute",
  inset: 0,
  margin: "auto",
  width: "92%",
  maxWidth: "420px",
  height: "fit-content",
  bgcolor: "transparent",
  border: "none",
  outline: "none",
  boxShadow: "none",
};

function BadgeEarnedModal(props) {
  const { open, onClose, badgeName, trackName, language } = props;

  const t = (english, spanish, chinese) => {
    if (language === "chinese") return chinese;
    if (language === "spanish") return spanish;
    return english;
  };

  const canvasRef = React.useRef(null);
  const confettiFnRef = React.useRef(null);

  React.useEffect(() => {
    if (!open || !canvasRef.current) return undefined;
    confettiFnRef.current = confetti.create(canvasRef.current, {
      resize: true,
      useWorker: true,
    });
    const fire = (particleRatio, opts) => {
      confettiFnRef.current?.({
        origin: { y: 0.6 },
        particleCount: Math.floor(240 * particleRatio),
        colors: ["#a78bfa", "#8b5cf6", "#fbbf24", "#f59e0b", "#ffffff"],
        ...opts,
      });
    };
    const timer = setTimeout(() => {
      fire(0.25, { spread: 30, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
      fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
      fire(0.1, { spread: 120, startVelocity: 45 });
    }, 250);
    return () => {
      clearTimeout(timer);
      confettiFnRef.current?.reset();
      confettiFnRef.current = null;
    };
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="badge-earned-modal">
      <Box sx={style}>
        <canvas
          ref={canvasRef}
          className="pointer-events-none absolute -inset-x-16 -top-40 -bottom-24 w-auto"
          style={{ width: "calc(100% + 8rem)", height: "calc(100% + 16rem)" }}
        />
        {/* The badge: parchment card with a double frame and gold seal */}
        <div className="relative rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-amber-50 p-1.5 shadow-2xl">
          <div className="rounded-xl border border-amber-200/80 px-6 pt-10 pb-8 text-center relative overflow-hidden bg-white/60">
            {/* Soft glow behind the seal */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full bg-amber-300/25 blur-2xl" />

            {/* Gold seal */}
            <div className="relative mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 flex items-center justify-center ring-8 ring-amber-200/60 shadow-lg">
              <WorkspacePremiumIcon className="text-white drop-shadow" style={{ fontSize: 42 }} />
            </div>

            <p className="relative mt-6 text-[11px] font-bold uppercase tracking-[0.22em] text-amber-600">
              {t("Badge earned", "Insignia obtenida", "获得徽章")}
            </p>
            <h2 className="relative mt-2 text-2xl font-bold text-slate-900 leading-snug">
              {t("Congratulations!", "¡Felicidades!", "恭喜！")}
            </h2>
            <p className="relative mt-3 text-sm text-slate-600">
              {t(
                "You earned the badge",
                "Has obtenido la insignia",
                "你已获得徽章："
              )}
            </p>
            <p className="relative mt-1 text-lg font-semibold text-slate-900">
              {badgeName}
            </p>

            <div className="relative mx-auto mt-5 h-px w-2/3 bg-gradient-to-r from-transparent via-amber-300 to-transparent" />

            {trackName ? (
              <p className="relative mt-4 text-xs text-slate-500">
                {t("Awarded for completing", "Otorgado por completar", "完成培训获得：")}{" "}
                <span className="font-medium text-slate-800">{trackName}</span>
              </p>
            ) : null}

            <button
              type="button"
              onClick={onClose}
              className="relative mt-7 w-full rounded-xl bg-violet-700 py-3 text-sm font-semibold text-white hover:bg-violet-800 transition-colors shadow"
            >
              {t("Keep going", "Continuar", "继续")}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default BadgeEarnedModal;
